import { z } from "zod";
import {
  AI_CONTEXT_REF_TYPES,
  AI_OUTPUT_SCHEMA_VERSION,
  REPLY_CLASSIFICATIONS,
  type AiContextRef,
  type AiSuggestionOutput,
} from "./contracts";
import type { AiBuiltContext } from "./context-builder";

/**
 * Schema estrito da saída da IA.
 *
 * A validação é local e total: saída fora do schema, incompleta ou com campo
 * desconhecido é erro tipado. Nenhum texto parcial é aproveitado.
 */

const trimmed = (max: number) => z.string().trim().min(1).max(max);

const optionalTrimmed = (max: number) =>
  z.preprocess((value) => (value === null ? undefined : value), trimmed(max).optional());

const optionalDate = z.preprocess(
  (value) => (value === null ? undefined : value),
  z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/u, "detectedReturnDate deve usar o formato AAAA-MM-DD").optional(),
);

const factReferenceSchema = z
  .object({
    ref: z
      .object({
        type: z.enum(AI_CONTEXT_REF_TYPES),
        id: z.string().trim().min(1).max(200),
        version: z.preprocess(
          (value) => (value === null ? undefined : value),
          z.number().int().nonnegative().optional(),
        ),
      })
      .strict(),
    field: trimmed(120),
  })
  .strict();

const factSchema = z
  .object({
    statement: trimmed(600),
    reference: factReferenceSchema,
  })
  .strict();

export const aiSuggestionOutputSchema = z
  .object({
    classification: z.enum(REPLY_CLASSIFICATIONS),
    confidence: z.number().min(0).max(1),
    summary: trimmed(1500),
    suggestedReply: z.string().trim().max(8000),
    recommendedApproach: trimmed(120),
    factsUsed: z.array(factSchema).max(30),
    assumptions: z.array(trimmed(500)).max(20),
    missingInformation: z.array(trimmed(500)).max(20),
    safetySignals: z.array(trimmed(200)).max(20),
    needsHumanReview: z.boolean(),
    // Structured Outputs representa campos opcionais como nullable e ainda os
    // exige em `required`. A validação local também aceita a omissão para
    // manter compatibilidade com resultados locais anteriores.
    handoffReason: optionalTrimmed(400),
    detectedReturnDate: optionalDate,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.needsHumanReview && !value.handoffReason)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["handoffReason"],
        message: "handoffReason é obrigatório quando needsHumanReview é verdadeiro",
      });
    if (value.detectedReturnDate && !isIsoCalendarDate(value.detectedReturnDate))
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["detectedReturnDate"],
        message: "detectedReturnDate inválida",
      });
  });

export type AiSuggestionOutputParsed = z.infer<typeof aiSuggestionOutputSchema>;

export type AiOutputValidation =
  | { ok: true; output: AiSuggestionOutput }
  | { ok: false; issues: string[] };

type RuntimeRecord = Record<string, unknown>;

const INVALID_NORMALIZED_INPUT = Symbol("invalid_ai_output_input");

function asRecord(value: unknown): RuntimeRecord | undefined {
  try {
    if (value === null || typeof value !== "object" || Array.isArray(value)) return undefined;
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) return undefined;
    if (Object.getOwnPropertySymbols(value).length > 0) return undefined;

    const normalized = Object.create(null) as RuntimeRecord;
    for (const key of Object.getOwnPropertyNames(value)) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!descriptor?.enumerable || !("value" in descriptor)) return undefined;
      normalized[key] = descriptor.value;
    }
    return normalized;
  } catch {
    return undefined;
  }
}

function denseArray(value: unknown): unknown[] | undefined {
  try {
    if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) return undefined;
    if (Object.getOwnPropertySymbols(value).length > 0) return undefined;

    const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
    if (
      !lengthDescriptor ||
      !("value" in lengthDescriptor) ||
      !Number.isSafeInteger(lengthDescriptor.value) ||
      lengthDescriptor.value < 0
    )
      return undefined;
    const length = lengthDescriptor.value;
    const ownNames = Object.getOwnPropertyNames(value);
    if (ownNames.length !== length + 1 || !ownNames.includes("length")) return undefined;
    for (const key of ownNames) {
      if (key === "length") continue;
      if (!/^(0|[1-9]\d*)$/u.test(key) || Number(key) >= length) return undefined;
    }

    const normalized: unknown[] = [];
    for (let index = 0; index < length; index += 1) {
      const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
      if (!descriptor?.enumerable || !("value" in descriptor)) return undefined;
      normalized.push(descriptor.value);
    }
    return normalized;
  } catch {
    return undefined;
  }
}

function normalizeJsonValue(
  value: unknown,
  seen: WeakSet<object>,
): unknown | typeof INVALID_NORMALIZED_INPUT {
  try {
    if (value === null) return null;
    if (typeof value !== "object") {
      return typeof value === "function" || typeof value === "symbol" || typeof value === "bigint"
        ? INVALID_NORMALIZED_INPUT
        : value;
    }
    if (seen.has(value)) return INVALID_NORMALIZED_INPUT;
    seen.add(value);

    if (Array.isArray(value)) {
      const array = denseArray(value);
      if (!array) return INVALID_NORMALIZED_INPUT;
      const normalized: unknown[] = [];
      for (const item of array) {
        const child = normalizeJsonValue(item, seen);
        if (child === INVALID_NORMALIZED_INPUT) return INVALID_NORMALIZED_INPUT;
        normalized.push(child);
      }
      seen.delete(value);
      return normalized;
    }

    const record = asRecord(value);
    if (!record) return INVALID_NORMALIZED_INPUT;
    const normalized = Object.create(null) as RuntimeRecord;
    for (const key of Object.getOwnPropertyNames(record)) {
      const child = normalizeJsonValue(record[key], seen);
      if (child === INVALID_NORMALIZED_INPUT) return INVALID_NORMALIZED_INPUT;
      normalized[key] = child;
    }
    seen.delete(value);
    return normalized;
  } catch {
    return INVALID_NORMALIZED_INPUT;
  }
}

function invalidOutput(): AiOutputValidation {
  return { ok: false, issues: ["(raiz): saída fora do contrato"] };
}

export function validateAiOutput(value: unknown): AiOutputValidation {
  try {
    const normalized = normalizeJsonValue(value, new WeakSet<object>());
    if (normalized === INVALID_NORMALIZED_INPUT) return invalidOutput();
    const parsed = aiSuggestionOutputSchema.safeParse(normalized);
    if (!parsed.success)
      return {
        ok: false,
        issues: parsed.error.issues.map((issue) => `${issue.path.join(".") || "(raiz)"}: ${issue.message}`),
      };
    return { ok: true, output: parsed.data as AiSuggestionOutput };
  } catch {
    return invalidOutput();
  }
}

function isIsoCalendarDate(value: string): boolean {
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

/**
 * Verifica se cada fato aponta para uma referência realmente presente no
 * contexto montado. Fato sem lastro é motivo de handoff, não de confiança.
 */
export function factsAreGrounded(
  output: AiSuggestionOutput,
  source: readonly AiContextRef[] | AiBuiltContext,
): { grounded: boolean; unknown: string[] } {
  try {
    const validation = validateAiOutput(output);
    if (!validation.ok) return invalidGrounding();
    const normalizedSource = normalizeJsonValue(source, new WeakSet<object>());
    if (normalizedSource === INVALID_NORMALIZED_INPUT) return invalidGrounding();

    let context: AiBuiltContext | undefined;
    let refs: readonly AiContextRef[];
    if (isContextRefList(normalizedSource)) {
      if (!validContextRefList(normalizedSource)) return invalidGrounding();
      refs = normalizedSource;
    } else {
      const sourceRecord = asRecord(normalizedSource);
      if (!sourceRecord || !validContextRefList(sourceRecord.refs)) return invalidGrounding();
      context = sourceRecord as unknown as AiBuiltContext;
      refs = sourceRecord.refs;
    }

    const known = new Set(refs.map((ref) => `${ref.type}:${ref.id}`));
    const unknown = validation.output.factsUsed
      .map((fact) => {
        const key = `${fact.reference.ref.type}:${fact.reference.ref.id}`;
        if (!known.has(key)) return key;
        if (context && !contextFieldExists(context, fact.reference.ref, fact.reference.field))
          return `${key}#${fact.reference.field}`;
        return null;
      })
      .filter((key): key is string => Boolean(key));
    return { grounded: unknown.length === 0, unknown: [...new Set(unknown)] };
  } catch {
    return invalidGrounding();
  }
}

function invalidGrounding(): { grounded: false; unknown: [string] } {
  return { grounded: false, unknown: ["invalid_input"] };
}

function isContextRefList(
  source: unknown,
): source is readonly AiContextRef[] {
  return Array.isArray(source);
}

function validContextRef(value: unknown): value is AiContextRef {
  const ref = asRecord(value);
  return Boolean(
    ref &&
      typeof ref.type === "string" &&
      AI_CONTEXT_REF_TYPES.includes(ref.type as (typeof AI_CONTEXT_REF_TYPES)[number]) &&
      typeof ref.id === "string" &&
      ref.id.trim().length > 0 &&
      ref.id.length <= 200 &&
      (ref.version === undefined ||
        (typeof ref.version === "number" && Number.isInteger(ref.version) && ref.version >= 0)),
  );
}

function validContextRefList(value: unknown): value is readonly AiContextRef[] {
  const array = denseArray(value);
  return array !== undefined && array.every(validContextRef);
}

function contextFieldExists(context: AiBuiltContext, ref: AiContextRef, field: string): boolean {
  let record: Record<string, unknown> | undefined;
  if (ref.type === "company" && context.facts.company?.ref.id === ref.id) record = asRecord(context.facts.company);
  else if (ref.type === "contact" && context.facts.contact?.ref.id === ref.id) record = asRecord(context.facts.contact);
  else if (ref.type === "lead" && context.facts.lead.ref.id === ref.id) record = asRecord(context.facts.lead);
  else if (ref.type === "funnel" && context.facts.funnel?.ref.id === ref.id) record = asRecord(context.facts.funnel);
  else if (ref.type === "stage" && context.facts.stage?.ref.id === ref.id) record = asRecord(context.facts.stage);
  else if (ref.type === "message") record = asRecord(context.conversation.find((item) => item.ref.id === ref.id));
  else if (ref.type === "event") record = asRecord(context.events.find((item) => item.ref.id === ref.id));
  else if (ref.type === "task") record = asRecord(context.tasks.find((item) => item.ref.id === ref.id));
  if (!record || !Object.prototype.hasOwnProperty.call(record, field)) return false;
  const value = record[field];
  return value !== undefined && value !== null && value !== "";
}

/**
 * JSON Schema estrito enviado ao provider. Ele é derivado das mesmas
 * constantes do domínio para não divergir da validação local.
 */
export function aiOutputJsonSchema(): Record<string, unknown> {
  const refSchema = {
    type: "object",
    additionalProperties: false,
    required: ["type", "id", "version"],
    properties: {
      type: { type: "string", enum: [...AI_CONTEXT_REF_TYPES] },
      id: { type: "string", minLength: 1, maxLength: 200 },
      version: { type: ["integer", "null"], minimum: 0 },
    },
  };
  return {
    type: "object",
    additionalProperties: false,
    required: [
      "classification",
      "confidence",
      "summary",
      "suggestedReply",
      "recommendedApproach",
      "factsUsed",
      "assumptions",
      "missingInformation",
      "safetySignals",
      "needsHumanReview",
      "handoffReason",
      "detectedReturnDate",
    ],
    properties: {
      classification: { type: "string", enum: [...REPLY_CLASSIFICATIONS] },
      confidence: { type: "number", minimum: 0, maximum: 1 },
      summary: { type: "string", minLength: 1, maxLength: 1500 },
      suggestedReply: { type: "string", maxLength: 8000 },
      recommendedApproach: { type: "string", minLength: 1, maxLength: 120 },
      factsUsed: {
        type: "array",
        maxItems: 30,
        items: {
          type: "object",
          additionalProperties: false,
          required: ["statement", "reference"],
          properties: {
            statement: { type: "string", minLength: 1, maxLength: 600 },
            reference: {
              type: "object",
              additionalProperties: false,
              required: ["ref", "field"],
              properties: { ref: refSchema, field: { type: "string", minLength: 1, maxLength: 120 } },
            },
          },
        },
      },
      assumptions: { type: "array", maxItems: 20, items: { type: "string", minLength: 1, maxLength: 500 } },
      missingInformation: { type: "array", maxItems: 20, items: { type: "string", minLength: 1, maxLength: 500 } },
      safetySignals: { type: "array", maxItems: 20, items: { type: "string", minLength: 1, maxLength: 200 } },
      needsHumanReview: { type: "boolean" },
      handoffReason: { type: ["string", "null"], minLength: 1, maxLength: 400 },
      detectedReturnDate: { type: ["string", "null"], pattern: "^\\d{4}-\\d{2}-\\d{2}$" },
    },
  };
}

export const AI_OUTPUT_SCHEMA_NAME = `cosmos_ai_suggestion_v${AI_OUTPUT_SCHEMA_VERSION}`;
